import { useState } from 'react'
import { Link } from 'react-router-dom'
import { getFiscalYearLabel, type BudgetCode } from '@budgetmonkey/shared'
import { Badge, Button, Card, ErrorNote, Money } from '@/components/ui'
import { formatDate } from '@/lib/dates'
import { ReceiptThumbnail } from '@/features/receipts/ReceiptThumbnail'
import {
  useLinkReceipt,
  useReceipts,
  useReceiptsForTransaction,
  useUnlinkReceipt,
} from '@/features/receipts/hooks'
import { convertHeicForUpload, uploadReceipt } from '@/features/receipts/api'
import { useCurrentUser } from '@/auth/AuthProvider'
import { usePeriods } from '@/features/reconciliation/hooks'
import { SOURCE_LABELS } from './TransactionList'
import {
  TransactionForm,
  type TransactionFormPrefill,
} from './TransactionForm'
import {
  useDeleteTransaction,
  useTransaction,
  useUpdateTransaction,
} from './hooks'
import { loadStateOf, LoadingOrOffline } from '@/lib/loadState'

/**
 * Transaction detail — workflow 7.5 step 5.
 *
 * The read view, the receipts attached to this charge, and the two corrections
 * a transaction needs: voiding one that was reversed, and deleting one entered
 * by mistake.
 */
export function TransactionDetail({
  transactionId,
  codesById,
  fiscalYear,
  onClose,
}: {
  transactionId: string
  codesById: ReadonlyMap<string, BudgetCode>
  fiscalYear: number
  onClose: () => void
}) {
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [editing, setEditing] = useState(false)
  const { data: periods = [] } = usePeriods()
  // Read live from the cache. Holding the transaction as a prop would leave
  // this screen showing pre-edit values after a void until the user navigated
  // away and back.
  const transactionQuery = useTransaction(transactionId, fiscalYear)
  const { data: transaction } = transactionQuery
  const state = loadStateOf(transactionQuery)
  const update = useUpdateTransaction(fiscalYear)
  const remove = useDeleteTransaction(fiscalYear)

  if (transaction === undefined) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={onClose} className="-ml-4">
          ← Back
        </Button>
        {state === 'ready' ? (
          <p className="text-slate-500" role="status">
            That transaction no longer exists.
          </p>
        ) : (
          <LoadingOrOffline state={state} what="This charge" />
        )}
      </div>
    )
  }

  const onSubmittedPacket = periods.some(
    (period) =>
      period.status === 'submitted' &&
      period.orderedTransactionIds.includes(transaction.id ?? ''),
  )

  const code = codesById.get(transaction.budgetCodeId)
  const isVoid = transaction.status === 'void'
  const error = update.error?.message ?? remove.error?.message

  if (editing) {
    return (
      <div className="space-y-5">
        <div className="space-y-1">
          <Button variant="ghost" onClick={() => setEditing(false)} className="-ml-4">
            ← Back
          </Button>
          <h1 className="text-2xl font-semibold tracking-tight">Edit charge</h1>
        </div>

        <Card>
          <TransactionForm
            submitLabel="Save changes"
            initialCode={code ?? null}
            fiscalYear={fiscalYear}
            prefill={{
              vendor: transaction.vendor,
              amount: transaction.amount,
              transactionDate: transaction.transactionDate,
              dateReceived: transaction.dateReceived,
              source: transaction.source as TransactionFormPrefill['source'],
              description: transaction.description,
              notes: transaction.notes,
              fiscalYear: transaction.fiscalYear,
            }}
            pending={update.isPending}
            error={update.error?.message}
            onCancel={() => setEditing(false)}
            onSubmit={(values) => {
              // Only the fields the form owns. Status, receipts, the packet's
              // vendor stamp and the CSIU confirmation are all set elsewhere,
              // and sending the whole object back would quietly revert them.
              update.mutate(
                {
                  id: transaction.id!,
                  patch: {
                    budgetCodeId: values.budgetCodeId,
                    transactionDate: values.transactionDate,
                    dateReceived: values.dateReceived,
                    amount: values.amount,
                    vendor: values.vendor,
                    description: values.description,
                    source: values.source,
                    notes: values.notes,
                    // Moving a charge between budget years changes which
                    // year's list it belongs to, so both have to be refreshed
                    // — the one it left and the one it joined.
                    ...(values.fiscalYear !== undefined
                      ? { fiscalYear: values.fiscalYear }
                      : {}),
                  },
                },
                { onSuccess: () => setEditing(false) },
              )
            }}
          />
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <div className="space-y-1">
        <Button variant="ghost" onClick={onClose} className="-ml-4">
          ← Back
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">{transaction.vendor}</h1>
        <p className="text-sm text-slate-500">{getFiscalYearLabel(fiscalYear)}</p>
      </div>

      {error !== undefined && <ErrorNote>{error}</ErrorNote>}

      <Card className="space-y-4">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <Money
            value={transaction.amount}
            className={`text-2xl font-semibold ${
              isVoid ? 'text-slate-400 line-through' : ''
            }`}
          />
          <div className="flex flex-wrap gap-2">
            {isVoid && <Badge tone="danger">Void</Badge>}
            {transaction.reconciledVia !== null && (
              <Badge tone="ok">
                Confirmed via {transaction.reconciledVia === 'packet' ? 'packet' : 'CSIU'}
              </Badge>
            )}
          </div>
        </div>

        <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
          <Detail label="Budget code">
            {code === undefined ? (
              <span className="text-slate-400">Unknown</span>
            ) : (
              <Link
                to={`/codes/${code.id}`}
                className="underline underline-offset-4 hover:text-slate-900"
              >
                {code.shortLabel}
              </Link>
            )}
            {code !== undefined && (
              <p className="font-mono text-xs text-slate-500">{code.code}</p>
            )}
            {code?.external === true && (
              <p className="text-xs text-amber-800">
                {code.externalOwnerName}'s code — excluded from your balances.
              </p>
            )}
          </Detail>

          <Detail label="Source">{SOURCE_LABELS[transaction.source]}</Detail>
          <Detail label="Date of purchase">
            {formatDate(transaction.transactionDate)}
          </Detail>
          <Detail label="Date item received">
            {formatDate(transaction.dateReceived)}
            {transaction.dateReceived === transaction.transactionDate && (
              <span className="text-xs text-slate-400"> (same as purchase)</span>
            )}
          </Detail>

          {transaction.description !== '' && (
            <Detail label="Description">{transaction.description}</Detail>
          )}
          {transaction.csiuJournalNumber !== null && (
            <Detail label="CSIU journal">{transaction.csiuJournalNumber}</Detail>
          )}
          {transaction.notes !== '' && (
            <Detail label="Notes">
              <span className="whitespace-pre-wrap">{transaction.notes}</span>
            </Detail>
          )}
        </dl>
      </Card>

      <ReceiptsForTransaction transactionId={transaction.id} />

      <div className="flex flex-wrap gap-3">
        <Button variant="primary" onClick={() => setEditing(true)}>
          Edit
        </Button>

        <Button
          onClick={() =>
            update.mutate({
              id: transaction.id!,
              patch: { status: isVoid ? 'pending' : 'void' },
            })
          }
          disabled={update.isPending}
        >
          {isVoid ? 'Un-void' : 'Void this charge'}
        </Button>

        {/* A charge on a submitted packet cannot be deleted — the business
            office holds a document naming it. Said here rather than only
            refused on press, so the reason arrives before the attempt. */}
        {onSubmittedPacket ? null : confirmingDelete ? (
          <>
            <Button
              variant="danger"
              onClick={() => remove.mutate(transaction.id!, { onSuccess: onClose })}
              disabled={remove.isPending}
            >
              {remove.isPending ? 'Deleting…' : 'Yes, delete permanently'}
            </Button>
            <Button variant="ghost" onClick={() => setConfirmingDelete(false)}>
              Cancel
            </Button>
          </>
        ) : (
          <Button variant="danger" onClick={() => setConfirmingDelete(true)}>
            Delete
          </Button>
        )}
      </div>

      {onSubmittedPacket && (
        <p className="max-w-prose text-xs text-amber-800">
          This charge is on a packet that has already been submitted, so it
          cannot be deleted — the business office holds a document listing it,
          and removing it here would leave the two disagreeing. Void it instead
          if it was reversed.
        </p>
      )}

      <p className="max-w-prose text-xs text-slate-500">
        Void keeps the row in the ledger but removes it from every total — the right
        choice for a charge that was reversed. Delete is for a transaction entered by
        mistake and cannot be undone.
      </p>
    </div>
  )
}

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
        {label}
      </dt>
      <dd className="mt-0.5 text-slate-900">{children}</dd>
    </div>
  )
}

/**
 * The receipts attached to this charge — §7.2, §8.1.
 *
 * This said "Receipt capture arrives in Phase 2" long after Phase 2 shipped,
 * so a charge with a receipt behind it looked like a charge with none. Worth
 * showing properly: a packet is only submittable when every item has either a
 * receipt or a deliberate reason for having none, and this is where that
 * becomes visible one transaction at a time.
 */
function ReceiptsForTransaction({ transactionId }: { transactionId: string | undefined }) {
  const { data: receipts = [], isLoading } = useReceiptsForTransaction(transactionId)
  const unlink = useUnlinkReceipt()
  const link = useLinkReceipt()
  const user = useCurrentUser()
  const [attaching, setAttaching] = useState(false)
  const [attachError, setAttachError] = useState<string>()
  const [justSaved, setJustSaved] = useState<string>()

  /**
   * Attaching happens here rather than only on the Receipts screen.
   *
   * Detaching a photo and then having no way to put one back is a dead end,
   * and it is reachable in one click from the packet — where a missing receipt
   * is exactly the thing being fixed.
   */
  async function attach(files: readonly File[]) {
    if (transactionId === undefined || files.length === 0) return

    setAttaching(true)
    const failed: string[] = []

    // Sequential, so several pages of one bill keep the order they were
    // chosen — that is the order they print behind the log sheet line.
    for (const file of files) {
      try {
        const prepared = await convertHeicForUpload(file)
        const handle = uploadReceipt(user.uid, prepared, 'desktop_upload')
        await handle.done
        await link.mutateAsync({ receiptId: handle.receiptId, transactionId })
      } catch (cause) {
        failed.push(
          `${file.name} (${cause instanceof Error ? cause.message : String(cause)})`,
        )
      }
    }

    setAttaching(false)
    setAttachError(
      failed.length === 0 ? undefined : `Couldn't attach ${failed.join('; ')}`,
    )
    if (failed.length < files.length) {
      setJustSaved(`${files.length - failed.length} attached and saved.`)
    }
  }

  return (
    <Card className="space-y-3">
      <div>
        <h2 className="font-medium">
          Receipts{receipts.length > 0 && ` (${receipts.length})`}
        </h2>
        {/* Said out loud because there is no Save button here and there *is*
            one under Edit below — without this it reads as an unsaved form,
            and the honest answer to "do I need to save?" is invisible. */}
        <p className="text-xs text-slate-500">
          Attaching and detaching save straight away. Nothing to confirm.
        </p>
      </div>

      {isLoading ? (
        <p className="text-sm text-slate-500" role="status">
          Loading…
        </p>
      ) : receipts.length === 0 ? (
        <p className="text-sm text-slate-600">
          Nothing attached yet. Add one below, or note on the packet why there
          isn't one.
        </p>
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2">
          {receipts.map((receipt) => (
            <li key={receipt.id} className="space-y-2">
              <ReceiptThumbnail receipt={receipt} />
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="min-w-0 truncate text-sm">
                  {receipt.extractedVendor ?? 'Receipt'}
                  {receipt.extractedAmount !== null && (
                    <Money value={receipt.extractedAmount} className="ml-2 text-slate-500" />
                  )}
                </span>
                <Button
                  variant="ghost"
                  onClick={() =>
                    unlink.mutate({ receipt, transactionId: transactionId! })
                  }
                  disabled={unlink.isPending}
                >
                  Detach
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="border-t border-slate-100 pt-3">
        <label className="block text-sm font-medium" htmlFor="attach-receipt">
          {receipts.length === 0 ? 'Attach a receipt' : 'Attach another'}
        </label>
        <input
          id="attach-receipt"
          type="file"
          multiple
          accept="image/*,application/pdf"
          disabled={attaching || transactionId === undefined}
          className="mt-2 block w-full text-sm text-slate-500 file:mr-3 file:min-h-9 file:cursor-pointer file:rounded-lg file:border file:border-slate-300 file:bg-white file:px-3 file:text-sm file:font-medium file:text-slate-700 hover:file:bg-slate-50"
          onChange={(event) => {
            const files = Array.from(event.target.files ?? [])
            event.target.value = ''
            setJustSaved(undefined)
            void attach(files)
          }}
        />
        {attaching && (
          <p className="mt-2 text-sm text-slate-500" role="status">
            Uploading…
          </p>
        )}
        {!attaching && justSaved !== undefined && (
          <p className="mt-2 text-sm text-emerald-700" role="status">
            {justSaved}
          </p>
        )}
        {attachError !== undefined && <ErrorNote>{attachError}</ErrorNote>}

        <ReuseCapturedReceipt
          transactionId={transactionId}
          alreadyHere={receipts.map((r) => r.id ?? '')}
          onAttach={(receiptId) => {
            setJustSaved(undefined)
            if (transactionId !== undefined) {
              link.mutate({ receiptId, transactionId })
            }
          }}
          pending={link.isPending}
        />
      </div>
    </Card>
  )
}

/**
 * Put a bill you have already captured behind this charge as well.
 *
 * The ordinary case, not an exception: an airline bills a segment, a seat and
 * a fee separately; a hotel takes a deposit and then a balance; an Uber tip
 * lands a week after the fare. One piece of paper, several charges on the
 * statement. Uploading it once per charge is what this replaces — the same
 * document filed twice, corrected twice, and impossible to tell apart.
 *
 * Attaching does not move the receipt off whatever else it is on. The packet
 * prints it behind every log sheet line that lists it, which is what the paper
 * copy did when the same page was photocopied for each charge.
 */
function ReuseCapturedReceipt({
  transactionId,
  alreadyHere,
  onAttach,
  pending,
}: {
  transactionId: string | undefined
  alreadyHere: readonly string[]
  onAttach: (receiptId: string) => void
  pending: boolean
}) {
  const { data: receipts = [] } = useReceipts()
  const [open, setOpen] = useState(false)

  const here = new Set(alreadyHere)
  // Newest first: the bill covering this charge was almost certainly captured
  // recently, and a receipt already on this charge is not a choice.
  const candidates = receipts
    .filter((r) => !here.has(r.id ?? ''))
    .slice()
    .sort((a, b) => b.capturedAt.localeCompare(a.capturedAt))
    .slice(0, 25)

  if (transactionId === undefined || candidates.length === 0) return null

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-3 text-sm text-slate-600 underline underline-offset-4 hover:text-slate-900"
      >
        Or use a bill already captured — one bill can cover several charges
      </button>
    )
  }

  return (
    <div className="mt-3 space-y-2">
      <p className="text-xs text-slate-500">
        Pick the bill this charge is part of. It stays on any other charges
        it's already behind.
      </p>
      <ul className="max-h-64 divide-y divide-slate-100 overflow-y-auto rounded-lg border border-slate-200">
        {candidates.map((receipt) => (
          <li key={receipt.id}>
            <button
              type="button"
              disabled={pending}
              onClick={() => {
                onAttach(receipt.id!)
                setOpen(false)
              }}
              className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left text-sm transition hover:bg-slate-50 disabled:opacity-50"
            >
              <span className="min-w-0 truncate">
                {receipt.extractedVendor ?? 'Not read'}
                <span className="ml-2 text-xs text-slate-500">
                  {formatDate(receipt.capturedAt)}
                </span>
              </span>
              {receipt.extractedAmount !== null && (
                <Money value={receipt.extractedAmount} className="shrink-0 text-slate-500" />
              )}
            </button>
          </li>
        ))}
      </ul>
      <Button variant="ghost" onClick={() => setOpen(false)}>
        Cancel
      </Button>
    </div>
  )
}
